import axios from "axios";
import React, { useEffect, useState } from "react";
import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../../components/StorageHelper";
import useUserStore from "../../../services/userStore";
import AzureGenericRoles from "./Components/AzureGenericRoles";
import K8Roles from "./Components/K8Roles";
import { SiKubernetes } from "react-icons/si";
import { Shield as ShieldIcon } from "@mui/icons-material";
import TailwindFilterHeader from "../../../tailwind-ui/components/TailwindFilterHeader";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import { triggerCloudSync } from "../../../triggerCloudSync";
import useNotificationStore from "../../../services/notificationStore";
import CloudFilters from "../../../components/v2components/CloudFilters";
import CloudRoleSelectorBar from "../CloudRoleSelectorBar";
import "../../../components/v2components/Cloud/CloudStatus.css";
import { Navigate } from "react-router-dom";
import { getTenantCloudHomePath, isTenantUser } from "../../../utils/cloudRoleHelpers";
import EmptyStateCard from "../../../components/v2components/EmptyStateCard";
import { Cloud } from "lucide-react";

const AzureRoles = () => {
  const [selectedTenantName, setSelectedTenantName] = useState(null);
  const [tenantList, setTenantList] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [tabValue, setTabValue] = useState("Roles");
  const [roleFilter, setRoleFilter] = useState("all");
  const syncStatus = useCloudSyncStore((state) => state.syncStatus);
  let { azureConfig } = useNotificationStore.getState();
  const [subList, setSubList] = useState([]);
  const [selectedSubList, setSelectedSubList] = useState(null);
  const latestSubList =
    myLocalStorage.getItem("latestSubList")?.subscriptionName;
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

  const userData = useUserStore((state) => state.user);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const ondemandButton = async () => {
    if (!selectedSubList?.azureSubscriptionId) return;

    try {
      const data = await triggerCloudSync(
        "azure",
        "/api/azure-sync/onDemand",
        selectedTenantName,
      );
      console.log("Azure Sync complete:", data);
    } catch (error) {
      console.error("Azure Sync failed", error);
    }
  };

  const handleTenantChange = (value) => {
    const tenant = tenantList.find((tenant) => tenant.tenantName === value);
    setSelectedTenantName(value);
    if (tenant) myLocalStorage.setItem("latestTenant", tenant);
  };

  const handleSubscriptionChange = (value) => {
    if (!value) {
      setSelectedSubList(null);
      myLocalStorage.setItem(
        "latestSubList",
        JSON.stringify({ azureSubscriptionId: "", subscriptionName: "None" }),
      );
      return;
    }
    const sub = subList.find((subs) => subs?.azureSubscriptionId === value || subs?.subscriptionName === value);
    if (sub !== undefined) {
      setSelectedSubList(sub);
      myLocalStorage.setItem("latestSubList", JSON.stringify(sub));
    }
  };

  const fetchSubList = async () => {
    try {
      const response = await axios.get(
        `${NGROK}/api/azureResources/v1/all?tenantName=${latestTenant}&type=SUBSCRIPTION`,
      );

      const subs = response?.data || [];
      setSubList(subs);

      if (subs.length > 0) {
        let latest = null;

        try {
          latest =
            typeof latestSubList === "string"
              ? JSON.parse(latestSubList)
              : latestSubList;
        } catch (e) {
          latest = null;
        }

        const match = latest
          ? subs.find(
            (item) => item?.subscriptionName === latest?.subscriptionName,
          )
          : null;

        const selected = match || subs[0];

        setSelectedSubList(selected);

        myLocalStorage.setItem("latestSubList", JSON.stringify(selected));
      } else {
        setSelectedSubList(null);
        myLocalStorage.setItem(
          "latestSubList",
          JSON.stringify({ azureSubscriptionId: "", subscriptionName: "None" }),
        );
      }
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
      setSubList([]);
      setSelectedSubList(null);
      myLocalStorage.setItem(
        "latestSubList",
        JSON.stringify({ azureSubscriptionId: "", subscriptionName: "None" }),
      );
    }
  };

  useEffect(() => {
    if (tenantList.length === 1) {
      setSelectedTenantName(tenantList[0].tenantName);
    } else if (tenantList.length > 1) {
      const latestTenantName =
        myLocalStorage.getItem("latestTenant")?.tenantName;
      const tenant = tenantList.find((t) => t.tenantName === latestTenantName);
      setSelectedTenantName(
        tenant ? tenant.tenantName : tenantList[0].tenantName,
      );
    }
  }, [tenantList]);

  useEffect(() => {
    fetchSubList();
  }, [selectedTenantName, latestTenant]);

  useEffect(() => {
    const fetchTenants = async () => {
      const response = await axios.get(`${NGROK}/api/get-all-domains`);
      setTenantList(response.data);
    };
    fetchTenants();
  }, [userData]);


  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden p-6">
        <CloudFilters
          cloudProvider="azure"
          onTenantChange={handleTenantChange}
          onCloudAccountChange={handleSubscriptionChange}
          isAdmin={true}
          onSync={ondemandButton}
          syncDisabled={!selectedSubList?.azureSubscriptionId || syncStatus["azure"]}
          syncLoading={Boolean(selectedSubList?.azureSubscriptionId) && syncStatus["azure"]}
        />

        {!selectedSubList?.azureSubscriptionId ? (
          <EmptyStateCard
            icon={Cloud}
            title="No Azure subscription configured"
            description="Configure an Azure subscription for this tenant to view Azure roles."
          />
        ) : (
          <>
            <CloudRoleSelectorBar
              roleFilter={roleFilter}
              onRoleFilterChange={setRoleFilter}
              activeTab={tabValue}
              onTabChange={setTabValue}
              cloudProvider="azure"
            />

            {tabValue === "Roles" && (
              <AzureGenericRoles subscription={selectedSubList} roleFilter={roleFilter} />
            )}
            {tabValue === "K8" && (
              <K8Roles
                selectedTenantName={selectedTenantName}
                allapi={`/api/azureResources/v1/all?type=AZURE_KUBERNETES&tenantName=${selectedTenantName}`}
                rolesApi="/api/k8-resources/v1/roles/byClusterId"
                rolespoliciesApi="/api/k8-resources/v1/rolePolicies/byRoleUid"
                cloud="AZURE"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AzureRoles;
